// Billing-cycle helpers for AI agent add-ons (receptionist / sales agent).
// The cycle comes from addon.metadata: billed_half_yearly / billed_quarterly,
// anything else is billed monthly (and prorated with a 30-day cap).

export const AI_SALE_AGENT_SLUG = "ai-sale-agent";
export const AI_RECEPTIONIST_AGENT_SLUG = "ai-receptionist-agent";
export const AI_AGENT_ADDON_SLUGS = [
  AI_RECEPTIONIST_AGENT_SLUG,
  AI_SALE_AGENT_SLUG,
];

export const QUARTERLY_MIN_DAYS = 90;
export const HALF_YEARLY_MIN_DAYS = 180;

export const isQuarterlyAddon = (addon: any) =>
  addon?.metadata?.billed_quarterly === true;

export const isHalfYearlyAddon = (addon: any) =>
  addon?.metadata?.billed_half_yearly === true;

export const isYearlyAddon = (addon: any) =>
  addon?.metadata?.billed_yearly === true;

// Days an upfront-billed add-on is charged/valid for.
// null => billed monthly, i.e. prorated instead of charged in full.
export const getUpfrontBillingDays = (addon: any): number | null =>
  isHalfYearlyAddon(addon)
    ? HALF_YEARLY_MIN_DAYS
    : isQuarterlyAddon(addon)
      ? QUARTERLY_MIN_DAYS
      : null;

// Human-readable cycle name, e.g. for a badge on an already-purchased add-on.
export const getBillingCycleName = (addon: any) =>
  isYearlyAddon(addon)
    ? "Yearly"
    : isHalfYearlyAddon(addon)
      ? "Half-yearly"
      : isQuarterlyAddon(addon)
        ? "Quarterly"
        : "Monthly";

// Rank of the billing cadence, for spotting a downgrade on renewal.
export const getBillingRank = (addon: any) =>
  isYearlyAddon(addon)
    ? 4
    : isHalfYearlyAddon(addon)
      ? 3
      : isQuarterlyAddon(addon)
        ? 2
        : 1;

// Length of one billing cycle in days.
export const getCycleDays = (addon: any) =>
  isYearlyAddon(addon)
    ? 365
    : isHalfYearlyAddon(addon)
      ? 180
      : isQuarterlyAddon(addon)
        ? 90
        : 30;

/**
 * Renewal charge for an AI add-on: the full price of the billing cycle it is
 * renewed into. No proration — a renewal buys the whole cycle regardless of
 * how many days are left on the plan.
 */
export const getRenewalCharge = (addon: any) => ({
  cycleDays: getCycleDays(addon),
  // paise, excluding tax
  gross: addon?.gross_amount || 0,
});

export const getBillingCycleLabel = (addon: any) =>
  isYearlyAddon(addon)
    ? "/year"
    : isHalfYearlyAddon(addon)
      ? "/half-year"
      : isQuarterlyAddon(addon)
        ? "/quarter"
        : "/month";

// Upfront cycles need at least that many days left on the plan.
export const isAIAgentCycleDisabled = (addon: any, remainingDays: any) => {
  const minDays = getUpfrontBillingDays(addon);
  return (
    minDays != null &&
    remainingDays != null &&
    remainingDays < minDays &&
    AI_AGENT_ADDON_SLUGS.includes(addon?.slug)
  );
};

// AI minutes bundled with an add-on variant for its billing cycle.
export const getIncludedMinutes = (addon: any): number | null => {
  const minutes = addon?.metadata?.minutes_included;
  return typeof minutes === "number" && minutes > 0 ? minutes : null;
};

/**
 * `valid_till` of the subscription_addons join row that came back with an add-on.
 *
 * Sequelize names the through row after the model class, so it arrives as
 * `SubscriptionAddonModel` today; the other spellings are cheap insurance against that
 * name changing, and the last resort scans for any nested object carrying a `valid_till`
 * so the badge keeps working rather than silently disappearing. Returns null when the
 * payload has no validity at all — callers must render nothing rather than guess.
 */
export const getAddonValidTill = (addon: any): string | null => {
  const direct =
    addon?.SubscriptionAddonModel ??
    addon?.SubscriptionAddon ??
    addon?.subscription_addon ??
    addon?.subscription_addons;
  const validTill = direct?.valid_till ?? addon?.valid_till;
  if (validTill) return validTill;
  for (const value of Object.values(addon || {})) {
    if (value && typeof value === "object" && (value as any).valid_till) {
      return (value as any).valid_till;
    }
  }
  return null;
};

/**
 * Ids of AI add-on rows a newer row of the same slug has replaced.
 *
 * Only one AI add-on per slug is ever in force, but the API only detaches the old
 * row when the switch happens while it is still valid (`getActiveSubscriptionAddons`
 * filters on `valid_till >= now`). Switch cycles after the old one lapsed and the
 * stale row keeps `status: ACTIVE` forever, so the org appears to hold both a
 * monthly and a quarterly agent. The row with the latest `valid_till` is the real
 * one; the rest are history and must not be shown as active or offered for renewal.
 *
 * Ranking needs dates: when any row of a slug has no `valid_till`, none of them are
 * marked, since guessing would hide a live add-on.
 */
export const getSupersededAiAddonIds = (activeAddons: any[]): number[] => {
  const bySlug = new Map<string, any[]>();
  for (const addon of activeAddons || []) {
    if (!AI_AGENT_ADDON_SLUGS.includes(addon?.slug)) continue;
    bySlug.set(addon.slug, [...(bySlug.get(addon.slug) || []), addon]);
  }

  const superseded: number[] = [];
  for (const rows of bySlug.values()) {
    if (rows.length < 2) continue;
    if (rows.some((row) => !getAddonValidTill(row))) continue;
    const current = rows.reduce((latest, row) =>
      new Date(getAddonValidTill(row) as string).getTime() >
      new Date(getAddonValidTill(latest) as string).getTime()
        ? row
        : latest,
    );
    for (const row of rows) {
      if (row.id !== current.id) superseded.push(row.id);
    }
  }
  return superseded;
};
